import {useNavigate, useParams} from "react-router-dom";
import {Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useState} from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {ApiKeyService} from "crqlar-react-common/dist/services/identity/apiKey.service";
import {ApiKey} from "crqlar-react-common/dist/models/identity/apiKey.model";
import Button from "@mui/material/Button";

const ApiKeyDetails = () => {

    //region State

    useTrackScreenVisit('system api key details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const [showKeyDialog, setShowKeyDialog] = useState(false)

    const apiKeyData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ApiKeyService.getApiKeyById(id)
            } else {
                return {}
            }
        },
        create: ApiKeyService.createApiKey,
        update: ApiKeyService.updateApiKey,
        delete: (id) => ApiKeyService.deleteApiKey({id: [id]}),
        onDeleted: () => navigate('/system/apiKeys', {replace: true}),
        onSaved: (apiKey, wasCreateAction, prev) => {
            if (wasCreateAction) {
                setShowKeyDialog(true)
            }
        },
        isValid: (apiKey) => {
            return !!apiKey.name
        },
        cleanupBeforeSave: (apiKey) => {
            return {
                ...apiKey,
                name: apiKey.name?.trim()
            }
        }
    }, id)

    const apiKey = apiKeyData.item
    const setApiKey = apiKeyData.setItem
    const nameMissing = isRequiredTextMissing(apiKeyData, apiKey.name)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof ApiKey, value: any) => {
        setApiKey({...apiKey, [field]: value})
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={apiKey.name}
            data={apiKeyData}
            back={() => navigate('/system/apiKeys')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Internal label for identifying where this API key is used.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={apiKey.name || ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                {
                    !!apiKey.id &&
                    <Property
                        title={t('Key')}
                        tooltip={t('Public part of the API key. Copy it for integrations; the secret value is only shown once after creation.')}
                    >
                        <TextField
                            id={'key'}
                            size={'small'}
                            value={apiKey.apiSecretPub || ''}
                            fullWidth
                            required
                            InputProps={{
                                readOnly: true,
                                endAdornment: <CopyAdornment value={apiKey.apiSecretPub || ''}/>,
                            }}
                        />
                    </Property>
                }
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete API key')}
                description={t('This permanently deletes the API key. Integrations using it will immediately stop working. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={apiKeyData.saving}
                onDelete={() => apiKeyData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {apiKey.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    const apiKeyDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6,
            }}
        >
            {basicInfoSection}

            {
                !!apiKey.id &&
                <>
                    <Divider/>
                    {dangerZoneSection}
                </>
            }
        </Stack>
    )

    // Simple dialog which shows created key

    const onHideKeyDialog = () => {
        setShowKeyDialog(false)
        navigate(`/system/apiKeys/${apiKey.id}`, {replace: true})
    }

    const keyDialog = (
        <Dialog open={showKeyDialog} onClose={() => onHideKeyDialog()}>
            <DialogTitle>{t('API key created')}</DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Typography>
                        {t('API key has been created successfully.')}
                    </Typography>

                    <Alert severity="info">
                        {t('Please note down the secret key. You will not be able to see it again.')}
                    </Alert>

                    <TextField
                        value={apiKey.apiSecretPub}
                        fullWidth
                        disabled
                    />

                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onHideKeyDialog()} color="primary">
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {apiKeyDataUI}
            {keyDialog}
        </Box>
    );
}

export default ApiKeyDetails;
